<template>
  <div
    id="pageDashboard"
    v-if="hasPermission('STATS') || hasPermission('ADMIN_STATS')"
  >
    <v-tabs centered color="cyan" dark icons-and-text>
      <v-tabs-slider color="yellow"></v-tabs-slider>

      <v-tab href="#basics" v-if="hasPermission('STATS')">
        {{ $t("dashboardPage.tabBasic") }}
        <v-icon> fa fa-eye </v-icon>
      </v-tab>

      <v-tab
        href="#weekly_financial"
        v-if="hasPermission('WEEKLY_FINANCIAL_STATS')"
      >
        {{ $t("dashboardPage.tabFinancial") }}
        <v-icon> attach_money </v-icon>
      </v-tab>

      <v-tab href="#passengers" v-if="hasPermission('PASSENGERS_STATS')">
        {{ $t("dashboardPage.tabPassengers") }}
        <v-icon> fa fa-user-o </v-icon>
      </v-tab>

      <v-tab href="#captains" v-if="hasPermission('CAPTAIN_STATS')">
        {{ $t("dashboardPage.tabCaptains") }}
        <v-icon> fa fa-user-secret </v-icon>
      </v-tab>

      <v-tab href="#mixed" v-if="hasPermission('MIXED_STATS')">
        {{ $t("dashboardPage.tabMixed") }}
        <v-icon> fa fa-random </v-icon>
      </v-tab>

      <v-tab href="#osm" v-if="hasPermission('OSM_STATS')">
        {{ $t("dashboardPage.tabOsm") }}
        <v-icon> map </v-icon>
      </v-tab>

      <v-tab-item value="basics" lazy>
        <v-container grid-list-xl fluid>
          <v-layout row wrap>
            <v-flex lg12 sm12 xs12 v-if="pendingCategories.length > 0">
              <v-widget :title="$t('dashboardPage.pendingRequests')">
                <div slot="widget-content">
                  <v-chip v-for="(c, i) in pendingCategories" :key="'pend' + i" small :color="c.info ? 'grey' : 'red'"
                    text-color="white" style="cursor:pointer" @click="goToPending(c)">
                    {{ categoryLabel(c) }}: {{ c.count }}
                  </v-chip>
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('dashboardPage.signupsToday')">
                <div slot="widget-content" class="headline text-xs-center">
                  {{ stats.signUpsToday }}
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('dashboardPage.approvedPassengersToday')">
                <div slot="widget-content" class="headline text-xs-center">
                  {{ stats.approvedPassengersToday }}
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('dashboardPage.unapprovedPassengersEver')">
                <div slot="widget-content" class="headline text-xs-center">
                  {{ stats.unApprovedPassengersEver }}
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('dashboardPage.unapprovedPassengersToday')">
                <div slot="widget-content">
                  <unapproved-passengers-pie-chart
                    v-if="
                      stats.unApprovedPassengersWithIdsToday > 0 ||
                      stats.unApprovedPassengersWithoutIdsToday
                    "
                    :un-approved-passengers-with-ids-today="
                      stats.unApprovedPassengersWithIdsToday
                    "
                    :un-approved-passengers-without-ids-today="
                      stats.unApprovedPassengersWithoutIdsToday
                    "
                  />
                  <div v-else class="headline text-xs-center">0</div>
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('dashboardPage.bookingsCountYesterday')">
                <div slot="widget-content" class="headline text-xs-center">
                  <non-pooling-vs-pooling-bookings-pie-chart
                    v-if="
                      stats.bookingsYesterdayPerService &&
                      stats.bookingsYesterdayPerService.reduce(
                        (sum, r) => sum + r.value,
                        0
                      ) > 0
                    "
                    :value="stats.bookingsYesterdayPerService"
                  />
                  <div v-else class="headline text-xs-center">0</div>
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('dashboardPage.incompleteBookingsYesterday')">
                <div slot="widget-content" class="headline text-xs-center">
                  <rejected-vs-cancelled-bookings-pie-chart
                    v-if="
                      stats.rejectedBookingsYesterday > 0 ||
                      stats.cancelledBookingsYesterday > 0
                    "
                    :rejected-bookings="stats.rejectedBookingsYesterday"
                    :cancelled-bookings="stats.cancelledBookingsYesterday"
                    :all-bookings-count="stats.allBookingsYesterday"
                  />
                  <div v-else class="headline text-xs-center">0</div>
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('dashboardPage.averageRatingToday')">
                <div slot="widget-content" class="headline text-xs-center">
                  <template v-if="stats.averageRatingToday !== null">
                    {{ stats.averageRatingToday }}
                  </template>
                  <template v-else> {{ $t("dashboardPage.noRatingsToday") }} </template>
                </div>
              </v-widget>
            </v-flex>
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item
        v-if="hasPermission('WEEKLY_FINANCIAL_STATS')"
        value="weekly_financial"
        lazy
      >
        <v-container grid-list-xl fluid>
          <loading-component :value="loading"></loading-component>
          <choose-date v-model="date"></choose-date>
          <h1 style="text-align: center">
            {{ $t("dashboardPage.weeklyFinancial") }}
          </h1>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs6
              ><h2 style="text-align: center">
                {{ $t("dashboardPage.from") }}: {{ financial_stats.period[0] }}
              </h2></v-flex
            >
            <v-flex xs6
              ><h2 style="text-align: center">
                {{ $t("dashboardPage.to") }}: {{ financial_stats.period[1] }}
              </h2></v-flex
            >
          </v-layout>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("dashboardPage.revenue") }}:
                {{ financial_stats.revenue | money }}
              </h3></v-flex
            >
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("dashboardPage.fee") }}: {{ financial_stats.fee | money }}
              </h3></v-flex
            >
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("dashboardPage.payroll") }}:
                {{ financial_stats.payroll | money }}
              </h3></v-flex
            >
          </v-layout>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("dashboardPage.discount") }}:
                {{ financial_stats.discount | money }}
              </h3></v-flex
            >
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("dashboardPage.refund") }}:
                {{ financial_stats.refund | money }}
              </h3></v-flex
            >
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("dashboardPage.penalties") }}:
                {{ financial_stats.penalties | money }}
              </h3>
            </v-flex>
          </v-layout>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs6
              ><h3 style="text-align: center">
                {{ $t("dashboardPage.bonusCaptain") }}:
                {{ financial_stats.bonus_captain | money }}
              </h3>
            </v-flex>
            <v-flex xs6
              ><h3 style="text-align: center">
                {{ $t("dashboardPage.bonusPassenger") }}:
                {{ financial_stats.bonus_passenger | money }}
              </h3></v-flex
            >
          </v-layout>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs12
              ><h1 style="text-align: center">
                {{ $t("dashboardPage.profit") }}:
                {{ financial_stats.profit | money }}
              </h1></v-flex
            >
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item
        v-if="hasPermission('PASSENGERS_STATS')"
        value="passengers"
        lazy
      >
        <v-container grid-list-xl fluid>
          <v-layout row wrap>
            <v-flex xs12>
              <v-widget :title="$t('dashboardPage.customizations')">
                <v-layout slot="widget-content">
                  <v-flex xs12 md4>
                    <v-select
                      :items="groupByOptions"
                      :label="$t('dashboardPage.groupBy')"
                      v-model="groupBy"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate
                      clearable
                      v-model="startDate"
                      :label="$t('dashboardPage.startDate')"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate
                      clearable
                      v-model="endDate"
                      :label="$t('dashboardPage.endDate')"
                    />
                  </v-flex>
                </v-layout>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.bookings')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.bookingsCountPerDay]"
                    :background-colors="['#0cb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF']"
                    :labels="[$t('dashboardPage.bookingsCount')]"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.successfulBookings')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.successfulBookingsCountPerDay]"
                    :background-colors="['#0cb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('dashboardPage.bookingsCount')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.rejectedBookings')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.rejectedBookingsCountPerDay]"
                    :background-colors="['#0cb']"
                    :border-colors="['#FFF']"
                    :default-group-by="groupBy"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('dashboardPage.bookingsCount')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.acquiredVsApprovedPassengers')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.acquiredPassengersPerDay,
                      stats.approvedPassengersPerDay,
                    ]"
                    :background-colors="['#0cb', '#0fb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF', '#FFF']"
                    stacked
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[
                      $t('dashboardPage.acquiredPassengers'),
                      $t('dashboardPage.approvedPassengers'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.activePassengers')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.activePassengersPerDay]"
                    :background-colors="['#0cb']"
                    :can-group-by="false"
                    :border-colors="['#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('dashboardPage.activePassengers')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('dashboardPage.passengersMonthDatetime')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constPassengersPerMonthDatetime,
                      stats.returningPassengersPerMonthDatetime,
                      stats.newPassengersPerMonthDatetime,
                      stats.lostPassengersPerMonthDatetime,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[
                      $t('dashboardPage.labelConst'),
                      $t('dashboardPage.labelReturning'),
                      $t('dashboardPage.labelNew'),
                      $t('dashboardPage.labelLost'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('dashboardPage.passengersWeekDatetime')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constPassengersPerWeekDatetime,
                      stats.returningPassengersPerWeekDatetime,
                      stats.newPassengersPerWeekDatetime,
                      stats.lostPassengersPerWeekDatetime,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[
                      $t('dashboardPage.labelConst'),
                      $t('dashboardPage.labelReturning'),
                      $t('dashboardPage.labelNew'),
                      $t('dashboardPage.labelLost'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('dashboardPage.passengersMonthCreation')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constPassengersPerMonthCreation,
                      stats.returningPassengersPerMonthCreation,
                      stats.newPassengersPerMonthCreation,
                      stats.lostPassengersPerMonthCreation,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[
                      $t('dashboardPage.labelConst'),
                      $t('dashboardPage.labelReturning'),
                      $t('dashboardPage.labelNew'),
                      $t('dashboardPage.labelLost'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('dashboardPage.passengersWeekCreation')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constPassengersPerWeekCreation,
                      stats.returningPassengersPerWeekCreation,
                      stats.newPassengersPerWeekCreation,
                      stats.lostPassengersPerWeekCreation,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[
                      $t('dashboardPage.labelConst'),
                      $t('dashboardPage.labelReturning'),
                      $t('dashboardPage.labelNew'),
                      $t('dashboardPage.labelLost'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item v-if="hasPermission('CAPTAIN_STATS')" value="captains" lazy>
        <v-container grid-list-xl fluid>
          <v-layout row wrap>
            <v-flex xs12>
              <v-widget :title="$t('dashboardPage.customizations')">
                <v-layout slot="widget-content">
                  <v-flex xs12 md4>
                    <v-select
                      :items="groupByOptions"
                      :label="$t('dashboardPage.groupBy')"
                      v-model="groupBy"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate
                      clearable
                      v-model="startDate"
                      :label="$t('dashboardPage.startDate')"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate
                      clearable
                      v-model="endDate"
                      :label="$t('dashboardPage.endDate')"
                    />
                  </v-flex>
                </v-layout>
              </v-widget>
            </v-flex>

            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.captainsFirstRide')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.captainsWhoMadeTheirFirstBookingPerDay]"
                    :background-colors="['#0cb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('dashboardPage.captainsCount')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.ghostVsMeterRide')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.avgGhostRidesPerDay,
                      stats.avgMeterRidesPerDay,
                    ]"
                    :background-colors="['#0cb', '#0fb']"
                    :border-colors="['#FFF', '#FFF']"
                    :default-group-by="groupBy"
                    group-function="avg"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[
                      $t('dashboardPage.avgGhostRide'),
                      $t('dashboardPage.avgMeterRide'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.acquiredVsApprovedCaptains')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.acquiredCaptainsPerDay,
                      stats.approvedCaptainsPerDay,
                    ]"
                    :background-colors="['#0cb', '#0fb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF', '#FFF']"
                    stacked
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[
                      $t('dashboardPage.acquiredCaptains'),
                      $t('dashboardPage.approvedCaptains'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.activeCaptains')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.activeCaptainsPerDay]"
                    :background-colors="['#0cb']"
                    :border-colors="['#FFF']"
                    :can-group-by="false"
                    stacked
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('dashboardPage.activeCaptains')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.onlineCaptains')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.onlineCaptainsPerDay]"
                    :background-colors="['#0cb']"
                    :border-colors="['#FFF']"
                    :can-group-by="false"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('dashboardPage.onlineCaptains')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('dashboardPage.captainsWeekCheckLog')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constCaptainsPerWeekCheckLog,
                      stats.returningCaptainsPerWeekCheckLog,
                      stats.newCaptainsPerWeekCheckLog,
                      stats.lostCaptainsPerWeekCheckLog,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[
                      $t('dashboardPage.labelConst'),
                      $t('dashboardPage.labelReturning'),
                      $t('dashboardPage.labelNew'),
                      $t('dashboardPage.labelLost'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('dashboardPage.captainsMonthCheckLog')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constCaptainsPerMonthCheckLog,
                      stats.returningCaptainsPerMonthCheckLog,
                      stats.newCaptainsPerMonthCheckLog,
                      stats.lostCaptainsPerMonthCheckLog,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[
                      $t('dashboardPage.labelConst'),
                      $t('dashboardPage.labelReturning'),
                      $t('dashboardPage.labelNew'),
                      $t('dashboardPage.labelLost'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('dashboardPage.captainsWeekRide')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constCaptainsPerWeekRide,
                      stats.returningCaptainsPerWeekRide,
                      stats.newCaptainsPerWeekRide,
                      stats.lostCaptainsPerWeekRide,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[
                      $t('dashboardPage.labelConst'),
                      $t('dashboardPage.labelReturning'),
                      $t('dashboardPage.labelNew'),
                      $t('dashboardPage.labelLost'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('dashboardPage.captainsMonthRide')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constCaptainsPerMonthRide,
                      stats.returningCaptainsPerMonthRide,
                      stats.newCaptainsPerMonthRide,
                      stats.lostCaptainsPerMonthRide,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[
                      $t('dashboardPage.labelConst'),
                      $t('dashboardPage.labelReturning'),
                      $t('dashboardPage.labelNew'),
                      $t('dashboardPage.labelLost'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item v-if="hasPermission('MIXED_STATS')" value="mixed" lazy>
        <v-container grid-list-xl fluid>
          <v-layout row wrap>
            <v-flex xs12>
              <v-widget :title="$t('dashboardPage.customizations')">
                <v-layout slot="widget-content">
                  <v-flex xs12 md4>
                    <v-select
                      :items="groupByOptions"
                      :label="$t('dashboardPage.groupBy')"
                      v-model="groupBy"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate
                      clearable
                      v-model="startDate"
                      :label="$t('dashboardPage.startDate')"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate
                      clearable
                      v-model="endDate"
                      :label="$t('dashboardPage.endDate')"
                    />
                  </v-flex>
                </v-layout>
              </v-widget>
            </v-flex>

            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('dashboardPage.avgRating')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.avgCaptainRatingPerDay,
                      stats.avgPassengerRatingPerDay,
                    ]"
                    :background-colors="['#0cb', '#0FF']"
                    :default-group-by="groupBy"
                    group-function="avg"
                    :border-colors="['#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[
                      $t('dashboardPage.captainRate'),
                      $t('dashboardPage.passengerRate'),
                    ]"
                  />
                </div>
              </v-widget>
            </v-flex>
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item v-if="hasPermission('OSM_STATS')" value="osm" lazy>
        <v-container grid-list-xl fluid>
          <osm-stats></osm-stats>
        </v-container>
      </v-tab-item>
    </v-tabs>
  </div>
</template>

<script>
import VWidget from "@/components/VWidget";
import Material from "vuetify/es5/util/colors";
import moment from "moment";
import { companyNow, companyToday } from "@/util/companyTime";
import UnapprovedPassengersPieChart from "@/components/widgets/statistic/UnapprovedPassengersPieChart";
import NonPoolingVsPoolingBookingsPieChart from "@/components/widgets/statistic/NonPoolingVsPoolingBookingsPieChart";
import RejectedVsCancelledBookingsPieChart from "@/components/widgets/statistic/RejetedVsCancelledBookingsPieChart";
import BarWithGrouping from "@/components/widgets/statistic/BarWithGrouping";
import ChooseDate from "@/components/monaco/input/ChooseDate";
import LoadingComponent from "@/components/LoadingComponent";
import DataTable from "@/components/monaco/DataTable";
import OsmStats from "@/components/monaco/OsmStats";

export default {
  title() {
    return this.$t("dashboardPage.pageTitle");
  },
  components: {
    OsmStats,
    DataTable,
    LoadingComponent,
    VWidget,
    UnapprovedPassengersPieChart,
    NonPoolingVsPoolingBookingsPieChart,
    RejectedVsCancelledBookingsPieChart,
    BarWithGrouping,
    ChooseDate,
  },
  data() {
    return {
      groupBy: "month",
      startDate: null,
      endDate: null,
      color: Material,
      date: "",
      loading: false,
      pendingSummary: {},
      pendingTotal: 0,
      stats: {
        unApprovedPassengersEver: 0,
        unApprovedPassengersWithIdsToday: 0,
        unApprovedPassengersWithoutIdsToday: 0,
        approvedPassengersToday: 0,
        signUpsToday: 0,
        allBookingsYesterday: 0,
        cancelledBookingsYesterday: 0,
        poolingBookingsYesterday: 0,
        rejectedBookingsYesterday: 0,
        averageRatingToday: 0,
        activeCaptainsPerDay: [
          { value: 0, date: companyToday() },
        ],
        captainsWhoMadeTheirFirstBookingPerDay: [
          { value: 0, date: companyToday() },
        ],
        acquiredCaptainsPerDay: [
          { value: 0, date: companyToday() },
        ],
        approvedCaptainsPerDay: [
          { value: 0, date: companyToday() },
        ],
        acquiredPassengersPerDay: [
          { value: 0, date: companyToday() },
        ],
        approvedPassengersPerDay: [
          { value: 0, date: companyToday() },
        ],
        activePassengersPerDay: [
          { value: 0, date: companyToday() },
        ],
        avgGhostRidesPerDay: [
          { value: 0, date: companyToday() },
        ],
        avgMeterRidesPerDay: [
          { value: 0, date: companyToday() },
        ],
        avgPassengerRatingPerDay: [
          { value: 0, date: companyToday() },
        ],
        avgCaptainRatingPerDay: [
          { value: 0, date: companyToday() },
        ],
        onlineCaptainsPerDay: [
          { value: 0, date: companyToday() },
        ],
        bookingsCountPerDay: [
          { value: 0, date: companyToday() },
        ],
        successfulBookingsCountPerDay: [
          { value: 0, date: companyToday() },
        ],
        rejectedBookingsCountPerDay: [
          { value: 0, date: companyToday() },
        ],
        returningPassengersPerWeekDatetime: [
          { value: 0, date: companyToday() },
        ],
        newPassengersPerWeekDatetime: [
          { value: 0, date: companyToday() },
        ],
        constPassengersPerWeekDatetime: [
          { value: 0, date: companyToday() },
        ],
        lostPassengersPerWeekDatetime: [
          { value: 0, date: companyToday() },
        ],
        returningPassengersPerMonthDatetime: [
          { value: 0, date: companyToday() },
        ],
        newPassengersPerMonthDatetime: [
          { value: 0, date: companyToday() },
        ],
        constPassengersPerMonthDatetime: [
          { value: 0, date: companyToday() },
        ],
        lostPassengersPerMonthDatetime: [
          { value: 0, date: companyToday() },
        ],
        returningPassengersPerWeekCreation: [
          { value: 0, date: companyToday() },
        ],
        newPassengersPerWeekCreation: [
          { value: 0, date: companyToday() },
        ],
        constPassengersPerWeekCreation: [
          { value: 0, date: companyToday() },
        ],
        lostPassengersPerWeekCreation: [
          { value: 0, date: companyToday() },
        ],
        returningPassengersPerMonthCreation: [
          { value: 0, date: companyToday() },
        ],
        newPassengersPerMonthCreation: [
          { value: 0, date: companyToday() },
        ],
        constPassengersPerMonthCreation: [
          { value: 0, date: companyToday() },
        ],
        lostPassengersPerMonthCreation: [
          { value: 0, date: companyToday() },
        ],
        returningCaptainsPerWeekCheckLog: [
          { value: 0, date: companyToday() },
        ],
        newCaptainsPerWeekCheckLog: [
          { value: 0, date: companyToday() },
        ],
        constCaptainsPerWeekCheckLog: [
          { value: 0, date: companyToday() },
        ],
        lostCaptainsPerWeekCheckLog: [
          { value: 0, date: companyToday() },
        ],
        returningCaptainsPerMonthCheckLog: [
          { value: 0, date: companyToday() },
        ],
        newCaptainsPerMonthCheckLog: [
          { value: 0, date: companyToday() },
        ],
        constCaptainsPerMonthCheckLog: [
          { value: 0, date: companyToday() },
        ],
        lostCaptainsPerMonthCheckLog: [
          { value: 0, date: companyToday() },
        ],
        returningCaptainsPerWeekRide: [
          { value: 0, date: companyToday() },
        ],
        newCaptainsPerWeekRide: [
          { value: 0, date: companyToday() },
        ],
        constCaptainsPerWeekRide: [
          { value: 0, date: companyToday() },
        ],
        lostCaptainsPerWeekRide: [
          { value: 0, date: companyToday() },
        ],
        returningCaptainsPerMonthRide: [
          { value: 0, date: companyToday() },
        ],
        newCaptainsPerMonthRide: [
          { value: 0, date: companyToday() },
        ],
        constCaptainsPerMonthRide: [
          { value: 0, date: companyToday() },
        ],
        lostCaptainsPerMonthRide: [
          { value: 0, date: companyToday() },
        ],
        bookingsYesterdayPerService: [
          { service_id: 1, name: "Pooling", value: 12 },
        ],
      },
      financial_stats: {
        period: [
          companyNow().startOf("week").format("YYYY-MM-DD HH:mm:ss"),
          companyNow().endOf("week").format("YYYY-MM-DD HH:mm:ss"),
        ],
      },
    };
  },
  methods: {
    getFinancialStats() {
      this.loading = true;
      this.axios.get(this.api.ACTIONS.STATISTICS).then(({ data: { data } }) => {
        this.stats = data;
      });
      this.axios
        .get(
          "statistics/weekly_financial" +
            (this.date ? `?date=${this.date}` : "")
        )
        .then((res) => {
          this.financial_stats = Object.assign(
            this.financial_stats,
            res.data.data
          );
          this.financial_stats.profit =
            parseInt(this.financial_stats.revenue) +
            parseInt(this.financial_stats.bonus_passenger) +
            parseInt(this.financial_stats.fee) +
            parseInt(this.financial_stats.payroll) +
            parseInt(this.financial_stats.discount) +
            parseInt(this.financial_stats.refund) +
            parseInt(this.financial_stats.bonus_captain) +
            parseInt(this.financial_stats.penalties);
        })
        .catch((err) => console.log(err.message))
        .finally(() => (this.loading = false));
    },
    loadPendingSummary() {
      this.axios
        .get("pending-summary")
        .then(({ data }) => {
          const d = data.data || {};
          this.pendingTotal = d.total || 0;
          this.pendingSummary = d.summary || {};
        })
        .catch(() => {});
    },
    goToPending(c) {
      if (!c || !c.route) return;
      const target = { name: c.route };
      if (c.query) target.query = c.query;
      this.$router.push(target).catch(() => {});
    },
    // Backend sends both languages; pick by the active UI locale.
    categoryLabel(c) {
      return this.$i18n && this.$i18n.locale === "ar" ? c.label_ar || c.label : c.label;
    },
  },
  computed: {
    groupByOptions() {
      return [
        { text: this.$t("dashboardPage.day"), value: "day" },
        { text: this.$t("dashboardPage.week"), value: "week" },
        { text: this.$t("dashboardPage.month"), value: "month" },
        { text: this.$t("dashboardPage.quarter"), value: "quarter" },
        { text: this.$t("dashboardPage.year"), value: "year" },
      ];
    },
    pendingCategories() {
      return Object.values(this.pendingSummary || {}).filter((c) => c && c.count > 0);
    },
  },
  watch: {
    date() {
      this.getFinancialStats();
    },
  },
  mounted() {
    this.getFinancialStats();
    this.loadPendingSummary();
  },
};
</script>
